/**
 * pp-size-picker - type-to-filter size chooser for the PosterPalooza PDP.
 *
 * PP frames carry up to ~98 sizes in one <select>, labelled "11x14 Mat for 13x16
 * Frame". Even sorted, scanning that list is the biggest drop-off on the page. This
 * element sits next to the native <select>, filters as you type, and writes the
 * choice straight back to that <select> - so Dawn's VariantSelects keeps doing all
 * the real work (price, media, variant id, availability). We never touch variant logic.
 *
 * Progressive enhancement: the element stays display:none until JS marks it ready, so
 * with JS off the shopper gets the ordinary <select>.
 */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('pp-size-picker')) return;

  const clean = (s) => s.toLowerCase().replace(/[”"″′']/g, '');

  // "16 x 20" / "16*20" -> "16x20". Labels always use a real separator.
  const normLabel = (s) => clean(s).replace(/(\d)\s*(?:x|×|\*)\s*(\d)/g, '$1x$2');

  // Queries get one extra rule: bare "16 20" and "16 by 20" also mean 16x20.
  const normQuery = (s) =>
    clean(s)
      .replace(/(\d)\s*(?:x|×|\*|by)\s*(\d)/g, '$1x$2')
      .replace(/(\d)\s+(\d)/g, '$1x$2');

  const squash = (s) => s.replace(/\s+/g, '');

  // The art size is always the FIRST measurement in the label:
  // "11x14 Mat for 13x16 Frame" -> "11x14";  "8x10 Frame" -> "8x10".
  const artPart = (label) =>
    label
      .split(/\s+mat\s+for\s+|\s+for\s+/i)[0]
      .replace(/\s*frame\s*$/i, '')
      .trim();

  /**
   * CRO item #5, in-theme half: make the size change measurable.
   *
   * Fires into GA4 (G-VEE0MDDPJF) through whichever transport the page already has,
   * so we can answer "did the searchable picker actually help" from engagement even
   * while the purchase conversion is still being repaired in the ads lane:
   *   pp_size_search    - shopper typed in the picker (once per page)
   *   pp_size_selected  - a size was chosen, with method=search|native
   * Analytics only. It never blocks, and a missing gtag is a no-op.
   */
  const track = (name, params) => {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, params));
      }
    } catch (e) {
      /* analytics must never break the buy box */
    }
  };

  class PPSizePicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.select = document.getElementById(this.dataset.select);
      this.input = this.querySelector('.pp-sp__input');
      this.list = this.querySelector('.pp-sp__list');
      this.clearBtn = this.querySelector('.pp-sp__clear');
      this.status = this.querySelector('.pp-sp__status');
      if (!this.select || !this.input || !this.list || !this.clearBtn) return;

      this.items = Array.from(this.select.options, (o, i) => {
        const value = o.value;
        const art = artPart(value);
        return {
          i,
          value,
          art,
          rest: value.slice(art.length),
          // Dawn decorates a sold-out value ("10x10 - Unavailable"), so any drift
          // between the option's text and its value means it cannot be bought.
          soldOut: o.textContent.trim() !== value,
          hay: squash(normLabel(value)),
          artHay: squash(normLabel(art)),
        };
      });

      this.nativeWrap = this.select.closest('.select');
      if (this.nativeWrap) this.nativeWrap.classList.add('pp-sp-native');
      this.classList.add('pp-sp--ready');
      this.ready = true;
      this.isOpen = false;
      this.active = -1;
      this.matches = this.items.slice();

      this.bind();
      this.syncFromSelect();
    }

    disconnectedCallback() {
      if (this.onDocPointer) {
        document.removeEventListener('pointerdown', this.onDocPointer, true);
      }
    }

    bind() {
      this.input.addEventListener('focus', () => this.openList());
      this.input.addEventListener('click', () => this.openList());
      this.input.addEventListener('input', () => {
        this.filter(this.input.value);
        this.openList(true);
        this.clearBtn.hidden = this.input.value === '';
        if (!this.searchTracked && this.input.value.trim()) {
          this.searchTracked = true;
          track('pp_size_search', { option_count: this.items.length });
        }
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));

      // mousedown, not click: it beats the input's blur, so the pick still lands
      this.list.addEventListener('mousedown', (e) => {
        const li = e.target.closest('[data-idx]');
        if (!li) return;
        e.preventDefault();
        this.choose(Number(li.dataset.idx));
      });

      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.clearBtn.hidden = true;
        this.filter('');
        this.openList(true);
        this.input.focus();
      });

      this.onDocPointer = (e) => {
        if (!this.contains(e.target)) this.closeList();
      };
      document.addEventListener('pointerdown', this.onDocPointer, true);

      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.closeList();
      });

      // anything else that moves the select (deep link, back button, another app)
      this.select.addEventListener('change', () => {
        if (this.writing) return;
        this.syncFromSelect();
        // reached the native control instead of the picker - worth knowing
        track('pp_size_selected', { method: 'native', size: this.select.value });
      });
    }

    syncFromSelect() {
      this.input.value = this.select.value;
      this.clearBtn.hidden = this.select.value === '';
      this.filter('');
    }

    filter(query) {
      const q = squash(normQuery(query || ''));
      if (!q) {
        this.matches = this.items.slice();
        this.render();
        return;
      }
      // Rank: art size starts with the query, then art size contains it, then
      // anywhere else in the label. Without this, typing "16x20" surfaces every mat
      // whose FRAME is 16x20 above the one that actually fits 16x20 art.
      const starts = [];
      const inArt = [];
      const anywhere = [];
      for (const it of this.items) {
        const at = it.artHay.indexOf(q);
        if (at === 0) starts.push(it);
        else if (at > 0) inArt.push(it);
        else if (it.hay.includes(q)) anywhere.push(it);
      }
      this.matches = starts.concat(inArt, anywhere);
      this.render();
    }

    render() {
      const frag = document.createDocumentFragment();
      for (const it of this.matches) {
        const li = document.createElement('li');
        li.className = 'pp-sp__opt' + (it.soldOut ? ' is-sold-out' : '');
        li.id = `${this.id}-opt-${it.i}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', String(it.value === this.select.value));
        li.dataset.idx = String(it.i);

        const size = document.createElement('span');
        size.className = 'pp-sp__optsize';
        size.textContent = it.art;
        li.appendChild(size);

        if (it.rest) {
          const rest = document.createElement('span');
          rest.className = 'pp-sp__optrest';
          rest.textContent = it.rest;
          li.appendChild(rest);
        }
        if (it.soldOut) {
          const tag = document.createElement('span');
          tag.className = 'pp-sp__opttag';
          tag.textContent = 'Out of stock';
          li.appendChild(tag);
        }
        frag.appendChild(li);
      }

      if (!this.matches.length) {
        const none = document.createElement('li');
        none.className = 'pp-sp__none';
        none.textContent = 'No size matches. Try just the numbers, like 16x20.';
        frag.appendChild(none);
      }

      this.list.replaceChildren(frag);
      // back to the top on every re-filter: the best-ranked matches are first, and a
      // retained scroll position leaves them above the fold of the list
      this.list.scrollTop = 0;
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (this.status) {
        this.status.textContent = this.matches.length
          ? `${this.matches.length} sizes match`
          : 'No sizes match';
      }
    }

    openList(keepScroll) {
      this.list.hidden = false;
      this.isOpen = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (keepScroll) return;
      const cur = this.list.querySelector('[aria-selected="true"]');
      if (cur) cur.scrollIntoView({ block: 'center' });
    }

    closeList() {
      if (!this.isOpen) return;
      this.list.hidden = true;
      this.isOpen = false;
      this.active = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
      // an abandoned half-typed query must never read as the chosen size
      if (this.input.value !== this.select.value) this.syncFromSelect();
    }

    setActive(n) {
      const opts = this.list.querySelectorAll('[data-idx]');
      if (!opts.length) return;
      if (n < 0) n = opts.length - 1;
      if (n >= opts.length) n = 0;
      opts.forEach((el, i) => el.classList.toggle('is-active', i === n));
      this.active = n;
      this.input.setAttribute('aria-activedescendant', opts[n].id);
      opts[n].scrollIntoView({ block: 'nearest' });
    }

    onKey(e) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!this.isOpen) this.openList();
          this.setActive(this.active + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!this.isOpen) this.openList();
          this.setActive(this.active - 1);
          break;
        case 'Home':
          if (this.isOpen) {
            e.preventDefault();
            this.setActive(0);
          }
          break;
        case 'End':
          if (this.isOpen) {
            e.preventDefault();
            this.setActive(-1);
          }
          break;
        case 'Enter': {
          if (!this.isOpen) return;
          e.preventDefault();
          const opts = this.list.querySelectorAll('[data-idx]');
          const pick = this.active > -1 ? opts[this.active] : opts[0];
          if (pick) this.choose(Number(pick.dataset.idx));
          break;
        }
        case 'Escape':
          if (this.isOpen) {
            e.preventDefault();
            this.closeList();
          }
          break;
        case 'Tab':
          this.closeList();
          break;
      }
    }

    choose(idx) {
      const it = this.items[idx];
      if (!it) return;
      this.input.value = it.value;
      this.clearBtn.hidden = false;
      // Hand off to Dawn: setting the native select and firing change is exactly what
      // a shopper using the plain dropdown does, so every downstream update still runs.
      this.writing = true;
      this.select.value = it.value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.writing = false;
      track('pp_size_selected', {
        method: 'search',
        size: it.value,
        typed: this.searchTracked ? 1 : 0,
      });
      this.closeList();
      this.input.focus();
    }
  }

  customElements.define('pp-size-picker', PPSizePicker);
})();
